import React, { useMemo } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { userAPI } from 'api/profile/userAPI';
import { RoutePaths } from 'routes/routes';
import { useFormikContext } from 'formik';
import { ResetTabs, FormValuesModel } from '..';

// components
import { Text } from '@ui';
import { Link } from '@ui';
import ResendOtp from 'components/Additional/ResendOtp';
import SubmitButton from 'components/Buttons/SubmitButton';
import AuthContainer from 'components/Additional/AuthContainer';
import RequestResetPassword from './RequestResetPassword';
import SecurityCodeVerification from './SecurityCodeVerification';
import SecuritySelfieVerification from './SecuritySelfieVerification';
import RequestResetPasswordUsername from './RequestResetPasswordUsername';

const ResetPasswordSteps = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { values } = useFormikContext<FormValuesModel>();

  const title = useMemo(() => {
    switch (values.currentStep) {
      case ResetTabs.RequestResetPasswordUsername:
        return t('reset_password.steps.request_reset_password_username.title');
      case ResetTabs.CodeVerification:
        return t('reset_password.steps.otp_verification.email.title');
      case ResetTabs.SelfieVerification:
        return t('reset_password.steps.selfie_verification.title');
      case ResetTabs.ResetPassword:
        return t('reset_password.steps.new_password.title');
    }
  }, [values.currentStep]);

  const renderCurrentTab = (currentStep: ResetTabs) => {
    switch (currentStep) {
      case ResetTabs.RequestResetPasswordUsername:
        return <RequestResetPasswordUsername />;
      case ResetTabs.CodeVerification:
        return <SecurityCodeVerification />;
      case ResetTabs.SelfieVerification:
        return <SecuritySelfieVerification />;
      case ResetTabs.ResetPassword:
        return <RequestResetPassword />;
    }
  };

  const footer = useMemo(() => {
    if (values.currentStep === ResetTabs.RequestResetPasswordUsername) {
      return (
        <FooterLinkWrapper>
          <Link to={RoutePaths.Auth_Root}>
            {t('reset_password.go_back_to_signin')}
          </Link>
        </FooterLinkWrapper>
      );
    }

    return null;
  }, [values.currentStep]);

  const resendEmailOtp = async () => {
    await userAPI.requestResetUsername({ username: values.username });
  };

  return (
    <StyledAuthContainer footerText={footer}>
      <Title variant="h5" weight="semi-bold" gutterBottom>
        {title}
      </Title>

      <div>
        {renderCurrentTab(values.currentStep)}

        {values.currentStep !== ResetTabs.SelfieVerification && (
          <>
            <StyledSubmitBtn fullWidth>
              {values.currentStep === ResetTabs.ResetPassword
                ? t('reset_password.reset_password_button')
                : t('next', { ns: 'common' })}
            </StyledSubmitBtn>
            {values.currentStep === ResetTabs.CodeVerification && (
              <ResendOtp
                key="emailVerification"
                resendOtpCallback={resendEmailOtp}
                successMessage={t(
                  'reset_password.steps.otp_verification.email.otp_sent_message',
                )}
              />
            )}
          </>
        )}
      </div>
    </StyledAuthContainer>
  );
};

const Title = styled(Text)`
  display: block;
  text-align: center;
`;

const FooterLinkWrapper = styled.div`
  margin-top: ${({ theme }) => theme.marginXs};
  text-align: center;
`;

const StyledSubmitBtn = styled(SubmitButton)`
  margin-top: ${({ theme }) => theme.marginXs};
`;

const StyledAuthContainer = styled(AuthContainer)`
  min-height: 385px !important;
`;

export default ResetPasswordSteps;
